import { NgModule } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { ToastrModule } from 'ngx-toastr'
import { ClipboardModule } from 'ngx-clipboard'
import { ButtonsModule } from 'ngx-bootstrap/buttons'
import { BsDropdownModule } from 'ngx-bootstrap/dropdown'
import { ColorPickerModule } from 'primeng/colorpicker'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxSpinnerModule } from 'ngx-spinner'

// Modules
import { SharedUtilDevextremeModule } from '@evologi/shared/util-devextreme'
import { SharedUtilToolkitModule } from '@evologi/shared/util-toolkit'
import { SharedUtilTranslationModule } from '@evologi/shared/util-translation'
import {
  NgModalServiceInterface,
  NgNotificationServiceInterface,
} from '@evologi/shared/data-access-api'

// Services
import { NgModalService } from './services/ng.modal.service'
import { NgNotificationService } from './services/ng.notification.service'

// Directives
import { AdviceDirective } from './directives/advice.directive'
import { ClipboardDirective } from './directives/clipboard.directive'
import { UniqueValidator } from '@evologi/shared/data-access-api'
import { InputSourceDirective } from '@evologi/shared/data-access-api'

// Components
import { AccordionComponent } from './components/accordion/accordion.component'
import { AdviceComponent } from './components/advice/advice.component'
import { BadgeComponent } from './components/badge/badge.component'
import { ButtonComponent } from './components/button/button.component'
import { ButtonNgComponent } from './components/button/button-ng.component'
import { ButtonsGroupComponent } from './components/buttons-group/buttons-group.component'
import { ButtonsNavigationComponent } from './components/buttons-navigation/buttons-navigation.component'
import { CardComponent } from './components/card/card.component'
import { CheckBoxComponent } from './components/check-box/check-box.component'
import { ColorPickerComponent } from './components/color-picker/color-picker.component'
import { DateBoxComponent } from './components/date-box/date-box.component'
import { DialogComponent } from './components/dialog/dialog.component'
import { DropdownComponent } from './components/dropdown/dropdown.component'
import { FormGroupComponent } from './components/form-group/form-group.component'
import { FormRowComponent } from './components/form-row/form-row.component'
import { GridComponent } from './components/grid/grid.component'
import { HeadlineComponent } from './components/headline/headline.component'
import { HtmlEditorComponent } from './components/html-editor/html-editor.component'
import { InputComponent } from './components/input/input.component'
import { InputTextNgComponent } from './components/input/input-text-ng.component'
import { JumbotronComponent } from './components/jumbotron/jumbotron.component'
import { ListComponent } from './components/list/list.component'
import { ListGroupComponent } from './components/list-group/list-group.component'
import { ListGroupItemComponent } from './components/list-group-item/list-group-item.component'
import { LoaderComponent } from './components/loader/loader.component'
import { MenuNgComponent } from './components/menu-ng/menu-ng.component'
import { NavigationShoulderComponent } from './components/navigation-shoulder/navigation-shoulder.component'
import { PlaceholderComponent } from './components/placeholder/placeholder.component'
import { SelectBoxComponent } from './components/select-box/select-box.component'
import { SelectMultiBoxComponent } from './components/select-multi-box/select-multi-box.component'
import { TagBoxComponent } from './components/tag-box/tag-box.component'
import { TextAreaComponent } from './components/text-area/text-area.component'
import { StackComponent } from './components/stack/stack.component'
import { TableComponent } from './components/grid/table.component'
import { SkeletonModule } from 'primeng/skeleton'
import { CalendarComponent } from './components/calendar/calendar.component'
import { DateBoxNgComponent } from './components/date-box/date-box-ng.component'
import { CarrierLogoComponent } from './components/carrier-logo/carrier-logo.component'
import { CheckBoxNgComponent } from './components/check-box/check-box-ng.component'

// PrimeNG Modules
import { BadgeModule } from 'primeng/badge'
import { ButtonModule } from 'primeng/button'
import { CalendarModule } from 'primeng/calendar'
import { DialogModule } from 'primeng/dialog'
import { DialogService } from 'primeng/dynamicdialog'
import { DividerModule } from 'primeng/divider'
import { DropdownModule } from 'primeng/dropdown'
import { InputTextModule } from 'primeng/inputtext'
import { ListboxModule } from 'primeng/listbox'
import { MenuModule } from 'primeng/menu'
import { MessagesModule } from 'primeng/messages'
import { MessageModule } from 'primeng/message'
import { MultiSelectModule } from 'primeng/multiselect'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { InputTextareaModule } from 'primeng/inputtextarea'
import { TableModule } from 'primeng/table'
import { ToastModule } from 'primeng/toast'
import { TooltipModule } from 'primeng/tooltip'
import { SelectBoxNgComponent } from './components/select-box/select-box-ng.component'
import { SelectMultiBoxNgComponent } from './components/select-multi-box/select-multi-box-ng.component'
import { InputNumberModule } from 'primeng/inputnumber'
import { InputNumberNgComponent } from './components/input/input-number-ng.component'
import { TextAreaNgComponent } from './components/text-area/text-area-ng.component'
import { AvatarModule } from 'primeng/avatar'
import { ButtonsGroupNgComponent } from './components/buttons-group/buttons-group-ng.component'
import { SelectButtonModule } from 'primeng/selectbutton'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipsModule } from 'primeng/chips'
import { ProgressBarModule } from 'primeng/progressbar'
import { InputSwitchModule } from 'primeng/inputswitch'

import { SelectionNgComponent } from './components/select-box/selection-ng.component'
import { ListboxNgComponent } from './components/list/listbox.component'
import { CounterButtonComponent } from './components/counter-button/counter-button.component'
import { ModalHeaderPillComponent } from './components/modal-header-pill/modal-header-pill.component'
import { SectionTitleComponent } from './components/text/section-title.component'
import { ModalHeaderSwitchComponent } from './components/modal-header-switch/modal-header-switch.component'
import { CardNgComponent } from './components/card/card-ng.component'
import { MessagesNgComponent } from './components/messages-ng/messages-ng.component'
import { SharedDataAccessApiModule } from '@evologi/shared/data-access-api'
import { ChartModule } from 'primeng/chart'
import { StepperModule } from 'primeng/stepper'
import { WizardComponent } from './components/wizard/wizard.component'
import { WizardStepComponent } from './components/wizard/wizard-step.component'

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    // Modules
    SharedUtilDevextremeModule,
    SharedUtilToolkitModule,
    SharedUtilTranslationModule,
    // Loader
    NgxSkeletonLoaderModule,
    NgxSpinnerModule,
    // Ngx-bootstrap
    ButtonsModule.forRoot(),
    BsDropdownModule,
    // Clipboard
    ClipboardModule,
    // Toast
    ToastrModule.forRoot({
      positionClass: 'toast-top-center',
      preventDuplicates: true,
    }),
    // Native PrimeNG
    BadgeModule,
    ButtonModule,
    CalendarModule,
    DialogModule,
    DividerModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    MenuModule,
    MessageModule,
    MessagesModule,
    MultiSelectModule,
    ProgressSpinnerModule,
    TableModule,
    ListboxModule,
    ToastModule,
    TooltipModule,
    AvatarModule,
    SelectButtonModule,
    CheckboxModule,
    ChipsModule,
    ChartModule,
    SkeletonModule,
    InputSwitchModule,
    ProgressBarModule,
    SharedDataAccessApiModule,
    ColorPickerModule,
    StepperModule,

    // Directives
    InputSourceDirective,
  ],
  declarations: [
    // Directives
    AdviceDirective,
    ClipboardDirective,
    UniqueValidator,

    // Components
    AccordionComponent,
    AdviceComponent,
    BadgeComponent,
    ButtonComponent,
    ButtonsGroupComponent,
    ButtonsGroupNgComponent,
    ButtonsNavigationComponent,
    CardComponent,
    CardNgComponent,
    CheckBoxNgComponent,
    CheckBoxComponent,
    ColorPickerComponent,
    CounterButtonComponent,
    DateBoxComponent,
    DialogComponent,
    DropdownComponent,
    FormGroupComponent,
    FormRowComponent,
    GridComponent,
    HeadlineComponent,
    HtmlEditorComponent,
    InputComponent,
    JumbotronComponent,
    ListComponent,
    ListGroupComponent,
    ListGroupItemComponent,
    LoaderComponent,
    NavigationShoulderComponent,
    PlaceholderComponent,
    SelectBoxComponent,
    SelectBoxNgComponent,
    SelectMultiBoxComponent,
    SelectMultiBoxNgComponent,
    SelectionNgComponent,
    StackComponent,
    TagBoxComponent,
    TextAreaComponent,
    CalendarComponent,
    CarrierLogoComponent,
    ModalHeaderPillComponent,
    ModalHeaderSwitchComponent,
    SectionTitleComponent,

    // Custom Prime NG
    ButtonNgComponent,
    DateBoxNgComponent,
    InputNumberNgComponent,
    InputTextNgComponent,
    MenuNgComponent,
    MessagesNgComponent,
    ListboxNgComponent,
    TextAreaNgComponent,
    WizardComponent,
    WizardStepComponent,

    // Native Prime NG
    TableComponent,
  ],
  exports: [
    // Directives
    AdviceDirective,
    ClipboardDirective,
    UniqueValidator,

    NgxSpinnerModule,

    // Components
    AccordionComponent,
    AdviceComponent,
    BadgeComponent,
    ButtonComponent,
    ButtonsGroupComponent,
    ButtonsGroupNgComponent,
    ButtonsNavigationComponent,
    CardComponent,
    CardNgComponent,
    CheckBoxNgComponent,
    CheckBoxComponent,
    ColorPickerComponent,
    CounterButtonComponent,
    DateBoxComponent,
    DropdownComponent,
    FormGroupComponent,
    FormRowComponent,
    GridComponent,
    HeadlineComponent,
    HtmlEditorComponent,
    InputComponent,
    InputTextareaModule,
    JumbotronComponent,
    ListComponent,
    ListGroupComponent,
    ListGroupItemComponent,
    LoaderComponent,
    NavigationShoulderComponent,
    PlaceholderComponent,
    SelectBoxComponent,
    SelectBoxNgComponent,
    SelectMultiBoxComponent,
    SelectMultiBoxNgComponent,
    SelectionNgComponent,
    StackComponent,
    TagBoxComponent,
    TextAreaComponent,
    CalendarComponent,
    CarrierLogoComponent,
    ModalHeaderPillComponent,
    ModalHeaderSwitchComponent,

    // Custom Prime NG
    ButtonNgComponent,
    DateBoxNgComponent,
    InputNumberNgComponent,
    InputTextNgComponent,
    InputTextareaModule,
    MenuNgComponent,
    MessagesNgComponent,
    ListboxNgComponent,
    TextAreaNgComponent,
    SectionTitleComponent,
    WizardComponent,
    WizardStepComponent,

    // Native PrimeNG
    ButtonModule,
    CalendarModule,
    DialogModule,
    DividerModule,
    MenuModule,
    ToastModule,
    TableModule,
    AvatarModule,
    ListboxModule,
    InputSwitchModule,
    ChartModule,
    SkeletonModule,
    TableComponent,
  ],
  providers: [
    DialogService,
    {
      provide: NgModalServiceInterface,
      useExisting: NgModalService,
    },
    {
      provide: NgNotificationServiceInterface,
      useExisting: NgNotificationService,
    },
  ],
})
export class AdminUiModule {}
